import { useCallback, useEffect, useRef, useState } from "react";

import type { RfpCoverage } from "@platform/plan-grid-core";

type BenefitPlanEntry = RfpCoverage["benefitPlans"][number];

// A plan with no benefits is the BPG skeleton stand-in (see
// findNewPlansFromBpg) and should be replaced once fuller data arrives.
const isPlaceholderPlan = (plan: BenefitPlanEntry): boolean =>
  !plan.benefits || Object.keys(plan.benefits).length === 0;

const normalizeClassName = (name: string): string => name.trim().toLowerCase();

const referencesRemovedClass = (
  plan: BenefitPlanEntry,
  availableClassIds: ReadonlySet<string>,
  removedClassNames: ReadonlySet<string>,
): boolean => {
  const hasUnavailableEligibilityClass = (plan.eligibilityClasses ?? []).some(
    (benefitClass) => !availableClassIds.has(benefitClass.id),
  );
  const hasLabelNamingRemovedClass = (plan.benefitClassAvailability ?? []).some(
    (label) => removedClassNames.has(normalizeClassName(label)),
  );

  return hasUnavailableEligibilityClass || hasLabelNamingRemovedClass;
};

/**
 * Resolves the coverage to use when the coverage prop changes.
 *
 * If the local ref is newer (unsaved edits), keep it but always merge in the
 * latest benefitClasses so Class Manager additions/deletions/renames are
 * reflected immediately. Also merges any NEW benefit plans from the incoming
 * coverage, and upgrades any placeholder plans already in current with the
 * fuller version from incoming once it's available. A plan that references a
 * class no longer on the coverage, through its eligibility classes or an
 * availability label naming it, takes both from incoming, since the backend
 * already reassigned them.
 */
export const resolveCoveragePropChange = (
  incoming: RfpCoverage | undefined,
  current: RfpCoverage | undefined,
): RfpCoverage | undefined => {
  if (
    incoming?.updatedAt &&
    current?.updatedAt &&
    incoming.type === current.type &&
    new Date(current.updatedAt).getTime() >
      new Date(incoming.updatedAt).getTime()
  ) {
    // Keep current (newer) state but merge benefitClasses and any new plans
    // from incoming. New plans appear when create/duplicate resolves the RFP
    // query after the local state was last edited.
    const currentPlanIds = new Set(
      current.benefitPlans?.map((plan) => plan.id).filter(Boolean),
    );
    const newPlans =
      incoming.benefitPlans?.filter(
        (plan) => !!plan.id && !currentPlanIds.has(plan.id),
      ) ?? [];

    const incomingById = new Map(
      incoming.benefitPlans
        ?.filter((plan) => !!plan.id)
        .map((plan) => [plan.id, plan]),
    );
    const availableClassIds = new Set(
      incoming.benefitClasses.map((benefitClass) => benefitClass.id),
    );
    const removedClassNames = new Set(
      current.benefitClasses
        .filter((benefitClass) => !availableClassIds.has(benefitClass.id))
        .map((benefitClass) => normalizeClassName(benefitClass.name)),
    );

    let plansUpgraded = false;
    const upgradedPlans = current.benefitPlans?.map((plan) => {
      if (!plan.id) return plan;
      const fromIncoming = incomingById.get(plan.id);
      if (!fromIncoming) return plan;

      if (isPlaceholderPlan(plan) && !isPlaceholderPlan(fromIncoming)) {
        plansUpgraded = true;
        return fromIncoming;
      }

      if (referencesRemovedClass(plan, availableClassIds, removedClassNames)) {
        plansUpgraded = true;
        return {
          ...plan,
          eligibilityClasses: fromIncoming.eligibilityClasses ?? [],
          benefitClassAvailability:
            fromIncoming.benefitClassAvailability ??
            plan.benefitClassAvailability,
        };
      }

      return plan;
    });

    return {
      ...current,
      benefitClasses: incoming.benefitClasses,
      ...((newPlans.length > 0 || plansUpgraded) && {
        benefitPlans: [...(upgradedPlans || []), ...newPlans],
      }),
    };
  }

  return incoming;
};

export const usePlanDesignerCoverageState = (
  coverage?: RfpCoverage,
): {
  selectedCoverage: RfpCoverage | undefined;
  getCurrentCoverage: () => RfpCoverage | undefined;
  updateSelectedCoverage: (updatedCoverage?: RfpCoverage) => void;
} => {
  const selectedCoverageRef = useRef<RfpCoverage | undefined>(coverage);
  const [selectedCoverage, setSelectedCoverage] = useState<
    RfpCoverage | undefined
  >(coverage);

  const getCurrentCoverage = useCallback(() => selectedCoverageRef.current, []);

  const updateSelectedCoverage = useCallback(
    (updatedCoverage?: RfpCoverage) => {
      setSelectedCoverage(updatedCoverage);
      selectedCoverageRef.current = updatedCoverage;
    },
    [],
  );

  useEffect(() => {
    updateSelectedCoverage(
      resolveCoveragePropChange(coverage, selectedCoverageRef.current),
    );
  }, [coverage, updateSelectedCoverage]);

  return {
    selectedCoverage,
    getCurrentCoverage,
    updateSelectedCoverage,
  };
};
